/**
 * Borrador del pase de lista (Esteban, 2026-10-31: "que no se le borre a la gente las cosas").
 *
 * Guillén (Barbería SPS, sábado 7 am) marcó a sus 11 alumnos y el iPhone recargó la página al volver de la cámara:
 * perdió todo dos veces y terminó mandando la foto por WhatsApp. Ahora cada toque queda guardado en el teléfono
 * en ese momento, y al volver a abrir la lista se recupera solo:
 *
 * - Marcas, notas, quién ya fue marcado y alumnos extra → localStorage (escritura inmediata y síncrona: si el
 *   navegador mata la pestaña un instante después del toque, el toque ya está guardado).
 * - Fotos (ya comprimidas) → IndexedDB, que sí aguanta archivos.
 *
 * Por persona, horario y clase. Se borra al guardar con éxito o con "Descartar". Caduca a los 3 días.
 * Nada de esto va al servidor: la lista se registra en WAPP solo con Guardar, porque una lista a medias
 * registraría como ausentes a los que faltaba marcar.
 */

export type MarcaGuardada = { isPresent: boolean; nota: string; tocado: boolean };

export type BorradorDeLista = {
	at: number;
	/** La clase todavía no tenía lista cuando se empezó el borrador. */
	esNueva: boolean;
	marcas: { [enrollmentId: string]: MarcaGuardada };
	/** Alumnos extra agregados en esta sesión (los que todavía no tienen classId). */
	extras: { [temporalId: string]: any };
	fotos: number;
};

const PREFIJO = "wapp.paseDeLista.";
const CADUCA_MS = 3 * 24 * 3600e3;
const MAX_BORRADORES = 30;

const BASE = "wapp-pase-lista";
const ALMACEN = "fotos";

export const llaveDeBorrador = (usuarioId: number | string, scheduleId: number, sessionId: number): string =>
	usuarioId + "|" + scheduleId + "|" + sessionId;

/* ---------- Texto: localStorage ---------- */

const limpiarViejos = () => {
	try {
		const limite = Date.now() - CADUCA_MS;
		const vivos: { k: string; at: number }[] = [];
		for (let i = localStorage.length - 1; i >= 0; i--) {
			const k = localStorage.key(i);
			if (!k || !k.startsWith(PREFIJO)) continue;
			let at = 0;
			try { at = Number(JSON.parse(localStorage.getItem(k) || "{}").at) || 0; } catch { /* dañado */ }
			if (at < limite) {
				localStorage.removeItem(k);
				borrarFotos(k.slice(PREFIJO.length));
			} else {
				vivos.push({ k, at });
			}
		}
		vivos.sort((a, b) => b.at - a.at).slice(MAX_BORRADORES).forEach(({ k }) => {
			localStorage.removeItem(k);
			borrarFotos(k.slice(PREFIJO.length));
		});
	} catch { /* sin almacenamiento */ }
};

let yaLimpie = false;

export const leerBorrador = (llave: string): BorradorDeLista | null => {
	if (!yaLimpie) {
		yaLimpie = true;
		limpiarViejos();
	}
	try {
		const crudo = localStorage.getItem(PREFIJO + llave);
		if (!crudo) return null;
		const b = JSON.parse(crudo);
		if (!b || typeof b !== "object" || !b.marcas || Date.now() - Number(b.at) > CADUCA_MS) return null;
		return b as BorradorDeLista;
	} catch {
		return null;
	}
};

export const guardarBorrador = (llave: string, borrador: Omit<BorradorDeLista, "at">) => {
	try {
		localStorage.setItem(PREFIJO + llave, JSON.stringify({ ...borrador, at: Date.now() }));
	} catch { /* lleno o bloqueado: el pase de lista sigue funcionando, solo sin borrador */ }
};

export const descartarBorrador = (llave: string) => {
	try { localStorage.removeItem(PREFIJO + llave); } catch { /* nada */ }
	borrarFotos(llave);
};

/* ---------- Fotos: IndexedDB ---------- */

const abrir = (): Promise<IDBDatabase | null> =>
	new Promise((resolve) => {
		try {
			if (typeof indexedDB === "undefined") return resolve(null);
			const pedido = indexedDB.open(BASE, 1);
			pedido.onupgradeneeded = () => {
				if (!pedido.result.objectStoreNames.contains(ALMACEN)) pedido.result.createObjectStore(ALMACEN);
			};
			pedido.onsuccess = () => resolve(pedido.result);
			pedido.onerror = () => resolve(null);
			pedido.onblocked = () => resolve(null);
		} catch {
			resolve(null);
		}
	});

type FotoGuardada = { nombre: string; tipo: string; datos: Blob };

export const guardarFotos = async (llave: string, fotos: File[]): Promise<void> => {
	const db = await abrir();
	if (!db) return;
	try {
		await new Promise<void>((resolve) => {
			const tx = db.transaction(ALMACEN, "readwrite");
			const valor: FotoGuardada[] = fotos.map((f) => ({ nombre: f.name, tipo: f.type, datos: f }));
			if (valor.length === 0) tx.objectStore(ALMACEN).delete(llave);
			else tx.objectStore(ALMACEN).put(valor, llave);
			tx.oncomplete = () => resolve();
			tx.onerror = () => resolve();
			tx.onabort = () => resolve();
		});
	} catch { /* sin espacio: las marcas siguen guardadas */ }
	finally { db.close(); }
};

export const leerFotos = async (llave: string): Promise<File[]> => {
	const db = await abrir();
	if (!db) return [];
	try {
		return await new Promise<File[]>((resolve) => {
			const pedido = db.transaction(ALMACEN, "readonly").objectStore(ALMACEN).get(llave);
			pedido.onsuccess = () => {
				const valor: FotoGuardada[] = Array.isArray(pedido.result) ? pedido.result : [];
				resolve(valor.filter((f) => f && f.datos).map((f) => new File([f.datos], f.nombre, { type: f.tipo || f.datos.type })));
			};
			pedido.onerror = () => resolve([]);
		});
	} catch {
		return [];
	} finally {
		db.close();
	}
};

const borrarFotos = (llave: string) => {
	guardarFotos(llave, []);
};
